import { Link } from 'react-router-dom';
import { faEdit } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DaySequence } from '@core/generated/gql-types';
import { formatTimeRangeAndDuration } from '@core/util/jobScheduleDisplay.util';
import { useJobStore } from '@core/job/useJobStore';
import { formatDate, formatBreakTime } from '@/utils/utils';
import { useDaySequenceStore } from '@core/daySequence/useDaySequenceStore';
import { mergeSequenceGroupForDisplay } from '@core/util/daySequenceDisplayMerge.util';
import { actionLinkClassName } from '@core/theme/actionTheme.util';
import Button from '@/components/Button';

interface TimesheetsToActionListProps {
  jobId: string;
  actionRequiredDaySequences: DaySequence[];
  onApprove: (daySequenceId: string) => void;
}

const TimesheetsToActionList = ({
  jobId,
  actionRequiredDaySequences,
  onApprove,
}: TimesheetsToActionListProps) => {
  const getJobById = useJobStore((state) => state.getJobById);
  const job = getJobById(jobId);

  return (
    <div className="flex w-full flex-col justify-between space-y-2">
      {actionRequiredDaySequences.length > 0 ? (
        actionRequiredDaySequences.map((daySequence) => {
          return (
            <div
              key={daySequence.id}
              className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
            >
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Date</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(daySequence.startDate)}
                  </span>
                </div>
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Logged Time</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatTimeRangeAndDuration(
                        daySequence.clockInTime,
                        daySequence.clockOutTime,
                        job,
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Break Time</span>
                    <span className="text-sm font-medium text-gray-900">
                      {formatBreakTime(daySequence.breaks)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between gap-3 pt-2">
                  <Link
                    to={`/jobs/${jobId}?tab=timesheets`}
                    className={`flex cursor-pointer items-center gap-2 text-sm font-medium ${actionLinkClassName}`}
                  >
                    <FontAwesomeIcon icon={faEdit} className="ml-1 h-3 w-3" />
                    Edit Timesheet
                  </Link>
                  <Button
                    type="button"
                    variant="primary"
                    onClick={async () => await onApprove(daySequence.id)}
                    className="!w-auto !rounded-lg !py-2 !text-sm !font-medium"
                  >
                    Approve
                  </Button>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="flex h-14 w-full items-center justify-between gap-2 rounded-md bg-white p-2">
          <div className="flex items-center gap-2">
            <div className="pl-3 text-sm text-gray-600">
              No timesheets to action
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimesheetsToActionList;
